import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { Header, Footer, LandingHeader, LandingFooter, MobileStickyBar, ScrollToTop, stickyBarShownOn } from './components/Layout';
import { PromoBanner } from './components/PromoBanner';
import { PageViewTracker } from './components/PageViewTracker';
import { SiteDataProvider } from './lib/siteData';
// Home stays in the main bundle for an instant first paint; every other
// route is code-split so first-time visitors download ~60% less JavaScript.
import HomePage from './pages/HomePage';

const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ResidentialPage = lazy(() => import('./pages/ResidentialPage'));
const CommercialPage = lazy(() => import('./pages/CommercialPage'));
const IndustrialPage = lazy(() => import('./pages/IndustrialPage'));
const EmergencyPage = lazy(() => import('./pages/EmergencyPage'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const EstimatePage = lazy(() => import('./pages/EstimatePage'));
const ServiceAreasPage = lazy(() => import('./pages/ServiceAreasPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FinancingPage = lazy(() => import('./pages/FinancingPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminCatalogPage = lazy(() => import('./pages/AdminCatalogPage'));
const AdminUtilitiesPage = lazy(() => import('./pages/AdminUtilitiesPage'));
const AdminRebatesPage = lazy(() => import('./pages/AdminRebatesPage'));
const AdminEnergyPage = lazy(() => import('./pages/AdminEnergyPage'));
const AdminFinancingPage = lazy(() => import('./pages/AdminFinancingPage'));
const AdminEstimatesPage = lazy(() => import('./pages/AdminEstimatesPage'));
const AdminAccuracyPage = lazy(() => import('./pages/AdminAccuracyPage'));
const AdminReviewsPage = lazy(() => import('./pages/AdminReviewsPage'));
const AdminAffiliationsPage = lazy(() => import('./pages/AdminAffiliationsPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// Toast host (sonner, ~10 KB gzip) is off the critical path: nothing can toast before the user
// interacts, so it loads as its own chunk after the main bundle instead of inside it.
const Toaster = lazy(() => import('./components/ui/sonner').then((m) => ({ default: m.Toaster })));

// Paid-search landing page: minimal chrome (logo + tap-to-call header, slim legal footer, no
// promo banner, no primary nav) so the only things to do are the hero form and the phone.
// The mobile sticky call bar stays — it is one of the page's tap-to-call links.
const LANDING_PATHS = new Set(['/residential-hvac-installation']);
// Bare pages (SEO Batch B, Part 3.3): /review is the link the owner texts to customers — no promo
// banner, no header or menu, no footer, no sticky bar. Just the logo and the one ask.
const BARE_PATHS = new Set(['/review']);

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#F4F1EA]" role="status" aria-label="Loading page" data-testid="route-loading">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 rounded-full border-2 border-[#D7D2C8] border-t-[#3E6F8E] animate-spin" />
      <span className="font-mono-idb text-[11px] uppercase tracking-[0.18em] text-[#61676E]">Loading</span>
    </div>
  </div>
);

/* The whole app below the router: shared by the browser (App) and the Node pre-render (entry-server.js). */
export const Shell = () => {
  const { pathname } = useLocation();
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  const isAdmin = pathname.startsWith('/admin');
  const isLanding = LANDING_PATHS.has(cleanPath);
  const isBare = BARE_PATHS.has(cleanPath);
  const showChrome = !isAdmin && !isBare;
  const showStickyBar = showChrome && stickyBarShownOn(cleanPath); // Batch B, Part 4: not on /review, /contact, calculator steps
  return (
    <SiteDataProvider>
      <ScrollToTop />
      <PageViewTracker />
      {showChrome && !isLanding && <PromoBanner />}
      {showChrome && (isLanding ? <LandingHeader /> : <Header />)}
      <main>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/residential-hvac-installation" element={<ResidentialPage />} />
            <Route path="/commercial-hvac-installation" element={<CommercialPage />} />
            <Route path="/industrial-hvac-installation" element={<IndustrialPage />} />
            <Route path="/emergency-hvac-service" element={<EmergencyPage />} />
            <Route path="/hvac-maintenance-tune-up" element={<MaintenancePage />} />
            <Route path="/estimate-calculator" element={<CalculatorPage />} />
            <Route path="/estimate/:estimateId" element={<EstimatePage />} />
            <Route path="/service-areas" element={<ServiceAreasPage />} />
            <Route path="/service-areas/:slug" element={<LocationPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/terms-of-service" element={<TermsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/catalog" element={<AdminCatalogPage />} />
            <Route path="/admin/utilities" element={<AdminUtilitiesPage />} />
            <Route path="/admin/rebates" element={<AdminRebatesPage />} />
            <Route path="/admin/energy" element={<AdminEnergyPage />} />
            <Route path="/admin/financing" element={<AdminFinancingPage />} />
            <Route path="/admin/estimates" element={<AdminEstimatesPage />} />
            <Route path="/admin/accuracy" element={<AdminAccuracyPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/affiliations" element={<AdminAffiliationsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {showChrome && (isLanding ? <LandingFooter /> : <Footer />)}
      {showStickyBar && <MobileStickyBar />}
      <Suspense fallback={null}>
        <Toaster position="top-right" />
      </Suspense>
    </SiteDataProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
